import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import RuleFormDialog from './RuleFormDialog';
import RuleFilters from './rules/RuleFilters';
import RulesTable from './rules/RulesTable';
import ConfirmationModal from './shared/ConfirmationModal';
import { useAugmentationRules } from '../hooks/useAugmentationRules';
import { useRuleFilters } from '../hooks/useRuleFilters';

const AugmentationRulesPage = ({ customerId }) => {
  // Custom hooks for data and filtering
  const {
    rules,
    customers,
    rulesetTypes,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedRulesetTypeId,
    setSelectedCustomerId,
    setSelectedRulesetTypeId,
    fetchRules,
    deleteRule,
    toggleRuleActive,
    cloneRule,
    reorderRules,
  } = useAugmentationRules(customerId || null);

  const {
    filteredRules,
    selectedRuleType,
    setSelectedRuleType,
    searchTerm,
    setSearchTerm,
  } = useRuleFilters(rules);

  // Modal states
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);

  // Handlers
  const handleCreateRule = () => {
    setEditingRuleId(null);
    setShowRuleForm(true);
  };

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setShowRuleForm(true);
  };

  const handleDeleteRule = (rule) => {
    setRuleToDelete(rule);
  };

  const confirmDeleteRule = async () => {
    try {
      await deleteRule(ruleToDelete.id);
      setRuleToDelete(null);
    } catch (err) {
      // Error already handled in hook
      setRuleToDelete(null);
    }
  };

  const handleRuleFormSave = () => {
    setShowRuleForm(false);
    setEditingRuleId(null);
    fetchRules();
  };

  const handleRuleFormCancel = () => {
    setShowRuleForm(false);
    setEditingRuleId(null);
  };

  return (
    <div className="user-list">
      <div className="user-list-header">
        <h2>Augmentation Rules</h2>
        <button 
          onClick={handleCreateRule} 
          className="btn btn-primary"
          disabled={!selectedCustomerId}
        >
          <FontAwesomeIcon icon={faPlus} /> Create Rule
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <RuleFilters
        customers={customerId ? [] : customers}
        rulesetTypes={rulesetTypes}
        selectedCustomerId={selectedCustomerId}
        selectedRulesetTypeId={selectedRulesetTypeId}
        selectedRuleType={selectedRuleType}
        searchTerm={searchTerm}
        onCustomerChange={customerId ? null : setSelectedCustomerId}
        onRulesetTypeChange={setSelectedRulesetTypeId}
        onRuleTypeChange={setSelectedRuleType}
        onSearchChange={setSearchTerm}
        loading={loading}
        hideCustomerFilter={!!customerId}
      />

      {loading && !customerId && customers.length === 0 ? (
        <div>Loading customers...</div>
      ) : !selectedCustomerId ? (
        <div className="alert alert-info">
          {customerId ? 'Loading rules...' : 'Please select a customer to view augmentation rules.'}
        </div>
      ) : loading ? (
        <div>Loading rules...</div>
      ) : filteredRules.length === 0 ? (
        <div className="alert alert-info">
          No augmentation rules found. Create your first rule to get started.
        </div>
      ) : (
        <RulesTable
          rules={filteredRules}
          rulesetTypes={rulesetTypes}
          onEdit={handleEditRule}
          onClone={cloneRule}
          onToggleActive={toggleRuleActive}
          onDelete={handleDeleteRule}
          onReorder={reorderRules}
        />
      )}

      {/* Rule Form Dialog */}
      {showRuleForm && (
        <RuleFormDialog
          ruleId={editingRuleId}
          customerId={selectedCustomerId}
          onSave={handleRuleFormSave}
          onCancel={handleRuleFormCancel}
          allRules={rules}
        />
      )}

      <ConfirmationModal
        show={!!ruleToDelete}
        title="Confirm Delete"
        message={
          <>
            <p>Are you sure you want to delete the rule <strong>"{ruleToDelete?.name}"</strong>?</p>
            <p className="text-muted">This action cannot be undone.</p>
          </>
        }
        confirmText="Delete Rule"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDeleteRule}
        onCancel={() => setRuleToDelete(null)}
      />
    </div>
  );
};

AugmentationRulesPage.propTypes = {
  customerId: PropTypes.string,
};

AugmentationRulesPage.defaultProps = {
  customerId: null,
};

export default AugmentationRulesPage;
